import React from 'react';
import ReactDOM from 'react-dom';
import { IBulletsAndNumberingDialog } from './dialog';
import { getMenuValues } from '@src/slide/application/service/menu-manager/menu-values';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { CharacterTypes, OfficeNumberType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/character-type';
import { handleModelMenuEvent } from '@src/slide/application/service/menu-manager/menu-event';
import { BulletProps, IBulletsAndNumberingDialogManager, IBulletsAndNumberingDialogManagerServiceDelegates } from './interface';
import { transaction } from '@src/slide/domain/slide-app';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { SOURCE_ID } from './constant';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

export class BulletsAndNumberingDialogManager implements IBulletsAndNumberingDialogManager {
    private dialogContainer = document.createElement('div');
    private loadDialogPromise: Promise<IBulletsAndNumberingDialog>;
    private originBulletProps: BulletProps | null;
    private services: IBulletsAndNumberingDialogManagerServiceDelegates;

    public constructor() {
        let loadDialogPromiseResolve: (value: IBulletsAndNumberingDialog) => void;
        this.loadDialogPromise = new Promise(resolve => loadDialogPromiseResolve = resolve);
        import(/* webpackChunkName: "lazyComponents" */ './dialog').then((module) => {
            loadDialogPromiseResolve(module.default);
        });
    }

    public setServiceDelegates(services: IBulletsAndNumberingDialogManagerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public showDialog(defaultTabId: string, sourceId: SOURCE_ID) {
        this.getSelectedBulletsProps();
        this.updateDialog(true, defaultTabId);
        // 上报打开来源
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.BULLETS_AND_NUMBERING_DIALOG,
            action: TDWActionType.BULLETS_AND_NUMBERING_DIALOG_OPEN_SOURCE,
            ver5: sourceId,
        });
    }

    public hideDialog = () => {
        this.originBulletProps = null;
        ReactDOM.unmountComponentAtNode(this.dialogContainer);
    };

    public async updateDialog(visible: boolean, defaultTabId: string) {
        const Dialog = await this.loadDialogPromise;
        const { color, size, startNumber, type, customText, customImage } = this.originBulletProps || {};
        ReactDOM.render(<Dialog
            visible={visible}
            defaultTabId={defaultTabId}
            defaultColor={color}
            defaultSize={size}
            defaultStartNumber={startNumber}
            defaultType={type}
            defaultCustomText={customText}
            defaultCustomImage={customImage}
            onClose={this.hideDialog}
            onOk={this.changeBulletProps}
            colorPickerManager={this.services.colorPickerManager}
            copyPasteImageService={this.services.copyPasteImageService}
        />, this.dialogContainer);
    }

    private changeBulletProps = (bulletProps: BulletProps) => {
        const selectedModels = this.getSelectedModels();
        if (!selectedModels?.length) {
            return;
        }
        const result: BulletProps = {};
        Object.keys(bulletProps).forEach((key) => {
            if (bulletProps[key] !== this.originBulletProps?.[key]) {
                result[key] = bulletProps[key];
            }
        });
        this.reportData(result);
        result.type = bulletProps.type;
        if (result.type === CharacterTypes.CUSTOM_IMAGE || result.type === CharacterTypes.CUSTOM_TEXT)  {
            // 自定义项目符号类型只是在这个地方用到，其他地方没使用也不识别，不需要保存
            delete result.type;
        } else {
            // 非自定义项目符号不需要自定义数据
            delete result.customText;
            delete result.customImage;
        }
        if (result.type && this.isNumbering(result.type)) {
            // 数字编号类型，设置的时候
            result.startNumber = result.startNumber || this.originBulletProps?.startNumber || 1;
        }
        transaction(() => {
            selectedModels.forEach((model) => {
                handleModelMenuEvent(model, {
                    actionType: ActionType.BULLET_SET,
                    values: result,
                    menuType: MenuType.TOOLBAR,
                });
            });
        });
        this.hideDialog();
    };

    /**
     * 获取当前选中的 model，考虑到备注选中的情况
     * @returns
     */
    private getSelectedModels = () => {
        const { selectionService } = this.services;
        const selectedArray = selectionService?.selectedElements.selectedArray;
        if (selectedArray) {
            return selectedArray;
        }
        const model = selectionService?.selectedElements.getCurrentModel();
        if (model.type === UIModelType.NOTE) {
            return [model];
        }
        return null;
    };

    /**
     * 数据上报
     * 传入的 bulletProps 需要是和原始值比对过的，避免无效数据上报
     * @param bulletProps
     */
    // eslint-disable-next-line complexity
    private reportData(bulletProps: BulletProps) {
        const { type, color, size, customText, customImage, startNumber } = bulletProps;
        const { type: originType } = this.originBulletProps || {};

        // 最终生效的类型
        const finalType = type || originType;
        if (type) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.BULLETS_AND_NUMBERING_DIALOG,
                action: TDWActionType.BULLETS_AND_NUMBERING_DIALOG_TYPE,
                ver5: type,
            });
        }
        if (color) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.BULLETS_AND_NUMBERING_DIALOG,
                action: TDWActionType.BULLETS_AND_NUMBERING_DIALOG_COLOR,
                ver5: color,
            });
        }
        if (size) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.BULLETS_AND_NUMBERING_DIALOG,
                action: TDWActionType.BULLETS_AND_NUMBERING_DIALOG_SIZE,
                ver5: size,
            });
        }
        // 只有最终生效类型是数字编号时，设置 startNumber 才是有效的
        if (startNumber && this.isNumbering(finalType)) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.BULLETS_AND_NUMBERING_DIALOG,
                action: TDWActionType.BULLETS_AND_NUMBERING_DIALOG_START_NUMBER,
                ver5: startNumber,
            });
        }
        // 只有最终生效是自定义类型时，设置自定义内容才是有效的
        if (
            (customText && finalType === CharacterTypes.CUSTOM_TEXT)
         || (customImage && finalType === CharacterTypes.CUSTOM_IMAGE)
        ) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.BULLETS_AND_NUMBERING_DIALOG,
                action: TDWActionType.BULLETS_AND_NUMBERING_DIALOG_CUSTOM_CONTENT,
                ver5: type === CharacterTypes.CUSTOM_TEXT ? customText : 'picture',
            });
        }
    }

    private getSelectedBulletsProps() {
        const { selectionService } = this.services;
        const model = selectionService.getSelectedModel();
        if (!model) {
            return;
        }
        const menuValues = getMenuValues(model);
        const {
            [ActionType.BULLET_COLOR]: color,
            [ActionType.BULLET_SIZE]: size,
            [ActionType.BULLET_START_NUMBER]: startNumber,
            [ActionType.ITEMNUMBER]: numberType,
            [ActionType.DIGITNUMBER]: bulletType,
            [ActionType.BULLET_CUSTOM_TEXT]: customText,
            [ActionType.BULLET_CUSTOM_IMAGE]: customImage,
        } = menuValues;
        this.originBulletProps = {
            color,
            size,
            startNumber,
            type: this.getBulletType(customText, customImage, numberType, bulletType),
            customText,
            customImage,
        };
    }

    private getBulletType(customText?: string, customImage?: string, numberType?: string, bulletType?: string) {
        if (customText) {
            return CharacterTypes.CUSTOM_TEXT;
        }
        if (customImage) {
            return CharacterTypes.CUSTOM_IMAGE;
        }
        return numberType || bulletType;
    }

    private isNumbering(type?: string) {
        if (!type) {
            return false;
        }
        return Object.values(OfficeNumberType).includes(type);
    }
}
